import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';

export interface Field {
    type: string;
    label: string;
    name: string;
    value: string | string[];
    options?: { name: string; id: string }[];
    required?: boolean;
    autoGenerate?: boolean;
    readOnly?: boolean;
}

interface EditPopupProps {
    title: string;
    fields: Field[];
    save: string;
    initialValues: { [key: string]: any };
    onSave: (updatedValues: { [key: string]: any }) => void;
    onClose: () => void;
}

const EditPopup: React.FC<EditPopupProps> = ({ title, fields, save, initialValues, onSave, onClose }) => {
    const { t, i18n } = useTranslation('driver');
    const [editFormValue, setEditFormValue] = useState<{ [key: string]: any }>({});
    const [showOptions, setShowOptions] = useState<{ [key: string]: boolean }>({});
    const multiSelectRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const parsedInitialValues = { ...initialValues };
        fields.forEach(field => {
            if (field.type === "multi-select" && Array.isArray(field.value)) {
                parsedInitialValues[field.label] = field.value.map(val => JSON.parse(val));
            }
        });
        setEditFormValue(parsedInitialValues);
    }, [initialValues, fields]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (multiSelectRef.current && !multiSelectRef.current.contains(event.target as Node)) {
                setShowOptions({});
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [multiSelectRef]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setEditFormValue({
            ...editFormValue,
            [name]: value,
        });
    };

    const handleMultiSelectChange = (e: React.ChangeEvent<HTMLInputElement>, label: string) => {
        const { value, checked } = e.target;
        const parsedValue = JSON.parse(value);
        setEditFormValue(prevState => {
            const selectedOptions = prevState[label] || [];
            if (checked) {
                return { ...prevState, [label]: [...selectedOptions, parsedValue] };
            } else {
                return { ...prevState, [label]: selectedOptions.filter((option: any) => option.id !== parsedValue.id) };
            }
        });
    };

    const toggleOptions = (label: string) => {
        setShowOptions(prevState => ({
            ...prevState,
            [label]: !prevState[label]
        }));
    };

    const removeOption = (label: string, option: any) => {
        setEditFormValue(prevState => ({
            ...prevState,
            [label]: prevState[label].filter((item: any) => item.id !== option.id)
        }));
    };

    const handleSaveChanges = () => {
        
            onSave(editFormValue);
            onClose();
        
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-50">
            <div className="bg-white rounded-md shadow-md w-full max-w-2xl relative">
                <button
                    className="absolute top-2 right-4 text-gray-600 hover:text-gray-800 text-title"
                    onClick={onClose}
                >
                    &times;
                </button>
                <h2 className="text-subtitle-1 font-semi-bold px-6 py-4">{title}</h2>
                <div className="border-y">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-y-0 px-6">
                        {fields.map((field, index) => (
                            <div key={index} className="my-4">
                                <label className="block mb-2">
                                    {field.label} {field.required && <span className="text-red-500">*</span>}
                                </label>
                                {field.type === "text" || field.type === "email" || field.type === "number" ? (
                                    <input
                                        type={field.type}
                                        name={field.label}
                                        className={`border p-2 rounded-md w-full ${field.readOnly ? 'bg-gray-200 text-gray-600' : ''}`}
                                        value={editFormValue[field.label] ?? field.value ?? ""}
                                        onChange={handleInputChange}
                                        readOnly={field.readOnly}
                                    />
                                ) : field.type === "calendar" ? (
                                    <input
                                        type="date"
                                        name={field.label}
                                        className={`border p-2 rounded-md w-full ${field.readOnly ? 'bg-gray-200 text-gray-600' : ''}`}
                                        value={editFormValue[field.label]?.split('T')[0] ?? field.value ?? ""}
                                        onChange={handleInputChange}
                                        readOnly={field.readOnly}
                                    />
                                ) : field.type === "dropdown" ? (
                                    <select
                                        name={field.label}
                                        className={`border p-2 rounded-md w-full ${field.readOnly ? 'bg-gray-200 text-gray-600' : ''}`}
                                        value={editFormValue[field.label] ?? field.value ?? ""}
                                        onChange={handleInputChange}
                                        disabled={field.readOnly}
                                    >
                                        {field.options?.map((option, idx) => (
                                            <option key={idx} value={option.name}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : field.type === "multi-select" ? (
                                    <div className="rounded-md w-full relative" ref={multiSelectRef}>
                                        <div
                                            className={`border p-2 rounded-md cursor-pointer ${field.readOnly ? 'bg-gray-200 text-gray-600' : ''} ${Array.isArray(editFormValue[field.label]) && editFormValue[field.label].length === 0 ? 'py-2' : ''}`}
                                            onClick={() => !field.readOnly && toggleOptions(field.label)}
                                        >
                                            {Array.isArray(editFormValue[field.label]) && editFormValue[field.label].length > 0
                                                ? editFormValue[field.label].map((option: any, idx: number) => (
                                                    <div key={idx} className="inline-block bg-gray-200 rounded px-2 mr-2 mb-0">
                                                        {option.name}
                                                        {!field.readOnly && (
                                                            <span
                                                                className="ml-1 text-red-500 cursor-pointer"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeOption(field.label, option);
                                                                }}
                                                            >
                                                                &times;
                                                            </span>
                                                        )}
                                                    </div>
                                                ))
                                                : <div className="py-3"></div>
                                            }
                                        </div>
                                        {showOptions[field.label] && !field.readOnly && (
                                            <div className="absolute mt-2 p-2 bg-white shadow-md w-full z-10 max-h-48 overflow-y-auto">
                                                {field.options?.map((option, idx) => (
                                                    <div key={idx} className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            name={field.label}
                                                            value={JSON.stringify(option)}
                                                            checked={editFormValue[field.label]?.some((o: any) => o.id === option.id) || false}
                                                            onChange={(e) => handleMultiSelectChange(e, field.label)}
                                                        />
                                                        <label>{option.name}</label>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex justify-between space-x-2 px-6 py-4 font-semi-bold">
                    <button
                        className="flex-1 py-2 px-4 bg-gray-300 text-primary/300 rounded-md"
                        onClick={onClose}
                    >
                        {t('edit.cancel')}
                    </button>
                    <button
                        className="flex-1 py-2 px-4 bg-primary/300 text-white rounded-md"
                        onClick={handleSaveChanges}
                    >
                        {save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditPopup;